import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, Subject, throwError } from 'rxjs';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { Bundle, BundleEntry, CodeableConcept, CodeSystem, HealthcareService, Medication, Practitioner, Reference, ValueSet, Location, Patient, Encounter, ServiceRequest, MedicationRequest, SupplyDelivery, Coding } from 'fhir/r4b';
import { ReferenceDataType } from './dynamic-forms.interface2';
import { backendEndPointToken, patientBackendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { UtilityService } from './utility.service';
import { SeriesVerticalComponent } from '@swimlane/ngx-charts';

@Injectable({
  providedIn: 'root'
})
export class FormFieldsSelectDataService {
  backendApiEndPoint = inject(backendEndPointToken);
  terminologyServer = inject(terminologyCanonicalUrlBaseToken);
  patientBackendEndPoint = inject(patientBackendEndPointToken);
  private http = inject(HttpClient);

  private readonly substanceValueSetUrls = {
    all: `${this.terminologyServer}/ValueSet/Substance/SubstanceCodeAll`,
    allCodeSystem: `${this.terminologyServer}/CodeSystem/Substance/SubstanceCode`,
    active: `${this.terminologyServer}/ValueSet/Substance/SubstanceCodeActive`,
  } as const;


  allUrls = {

    'activityDefinition': {
      'itemsRequired': `${this.backendApiEndPoint}/SupplyDelivery?_count=1000`

    },

    // SupplyDelivery / inventory helpers
    'supplyDelivery': {
      // Substance catalogue options (served by our backend; do NOT fetch from local files)
      // Uses ValueSet/$expand so server can apply `filter=` when used via CodeableConceptFieldFromBackEnd
      'substanceCode': `${this.backendApiEndPoint}/ValueSet?url=${encodeURIComponent(this.substanceValueSetUrls.all)}&_count=1000&_format=json`,
      // 'substanceCodeAll': `${this.backendApiEndPoint}/ValueSet/$expand?url=${encodeURIComponent(this.substanceValueSetUrls.all)}&_count=1000&_format=json`,
    },

    'referral': {
      'organization': `${this.backendApiEndPoint}/Organization?_format=json`,

    },
    'addClaim': {
      'encounter': `${this.backendApiEndPoint}/Encounter?_format=json`
    },

    'appointment': {
      'status': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/appointmentstatus&_format=json',
      'appointmentType': 'https://tx.fhir.org/r4/CodeSystem?url=http://terminology.hl7.org/CodeSystem/v2-0276&_format=json',
      'serviceType': `${this.backendApiEndPoint}/HealthcareService?_format=json`,
      'reason': "/dummy.json",
      'participantActor': `${this.backendApiEndPoint}/Practitioner?_format=json`,
      'slot': `${this.backendApiEndPoint}/Slot?_format=json`,

    },
    'slot': {
      'serviceType': `${this.backendApiEndPoint}/HealthcareService?_format=json`,
      'appointmentType': 'https://tx.fhir.org/r4/CodeSystem?url=http://hl7.org/fhir/ValueSet/encounter-reason&_format=json',



    },
    'healthcareService': {
      'practitioners': `${this.backendApiEndPoint}/Practitioner?_format=json&_count=1000`
    },
    'patient': {
      'gender': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/administrative-gender&_format=json",
      'maritalStatus': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/marital-status&_format=json",
      'relationship': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/patient-contactrelationship&_format=json",
      'general_practitioner': `${this.backendApiEndPoint}/Practitioner?_format=json`,
      'contactPointUse': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/contact-point-use&_format=json",
      'contactPointSystem': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/contact-point-system&_format=json",
      'organization': this.backendApiEndPoint + "/Organization?_format=json&_count=1000",


    },

    'encounter': {
      'class': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v3-ActEncounterCode&_format=json&_count=1000',
      'priority': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v3-ActPriority&_format=json',
      'participant': `${this.backendApiEndPoint}/Practitioner?active=true&_format=json&_count=100`,
      'reason': "/dummy.json",
      'reason_use': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/encounter-reason-use&_format=json'
    },
    'serviceRequest': {
      'status': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/request-status&_format=json",
      'intent': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/request-intent&_format=json",
      'priority': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/request-priority&_format=json",
      'code': "/dummy.json",
      'reason': "/dummy.json",
      'performerType': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/practitioner-role&_format=json",

    },
    'medication': {
      // 'medication': "https://snowstorm.ihtsdotools.org/fhir/ValueSet/$expand?url=http://snomed.info/sct?fhir_vs=isa/763158003&_format=json",
      'medication': "dummy.json",
      'status': "/medication/status.json",
      'intent': "/medication/intent.json",
      'performerType': "/medication/performerType.json",
      'reason': "/dummy.json",
      'code': "dummy.json"
    },
    'medication_dispense': {
      'status': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medicationdispense-status&_format=json",
      'subject': this.backendApiEndPoint + "/Patient?_format=json&_count=1000",
      'category': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medicationdispense-category&_format=json",
      'receiver': this.backendApiEndPoint + "/Patient?_format=json&_count=1000",
      // 'medication': "https://snowstorm.ihtsdotools.org/fhir/ValueSet/$expand?url=http://snomed.info/sct?fhir_vs=isa/763158003&_format=json",
      'medication': "dummy.json",
      medicationReference: this.backendApiEndPoint + "/Medication?_format=json&_count=1000",
      performer: this.backendApiEndPoint + "/Practitioner?_format=json&_count=1000",
      authorizingPrescription: this.backendApiEndPoint + "/MedicationRequest?_format=json&_count=1000",
      substitutionReason: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v3-SubstanceAdminSubstitutionReason&_format=json",
      substitutionType: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v3-ActSubstanceAdminSubstitutionCode&_format=json"


    },
    'medication_administration': {
      'status': "http://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medication-admin-status&_format=json",
      'medication': "/dummy.json",
      'subject': `${this.backendApiEndPoint}/Patient?_format=json`,
      "performer": `${this.backendApiEndPoint}/Practitioner?_format=json`,
      "request": `${this.backendApiEndPoint}/MedicationRequest?_format=json`

    },
    'condition': {
      'code': "/dummy.json",
      'clinicalStatus': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/condition-clinical&_format=json",
      'verificationStatus': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/condition-ver-status&_format=json",
      'severity': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/condition-severity&_format=json",
    },
    'specimen': {
      'status': "http://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/specimen-status&_FORMAT=JSON",
      "type": "http://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v2-0487&_FORMAT=JSON",
      "subject": `${this.backendApiEndPoint}/Patient?_format=json&_count=1000`,
      "request": `${this.backendApiEndPoint}/ServiceRequest?_format=json&_count=1000`,
      "collector": "/encounter/encounter_participant.json",
      "bodySite": "/dummy.json",
      "condition": "http://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v2-0493&_FORMAT=JSON"
    },

    'admission': {
      // 'location': `${this.backendApiEndPoint}/Location?_format=json`,
      'location': `${this.backendApiEndPoint}/Location?_format=json&_count=1000`,
      'admitSource': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/encounter-admit-source&_format=json",
      'carePlanStatus': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/request-status&_format=json",
      'carePlanIntent': "https://server.fire.ly/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/care-plan-intent&_format=json",
      // 'ward': `${this.backendApiEndPoint}/Location?_format=json&_count=1000`,
      // 'room': `${this.backendApiEndPoint}/Location?_format=json&_count=1000`,
      // 'bed': `${this.backendApiEndPoint}/Location?_format=json&_count=1000`,
      //careplansUBJECT: "",
      //taskintent, task priority, task status, task code
      'taskIntent': "https://server.fire.ly/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/task-intent&_format=json",
      'taskPriority': "https://server.fire.ly/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/request-priority&_format=json",
      'taskStatus': "https://server.fire.ly/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/task-status&_format=json",
      'taskCode': "https://server.fire.ly/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/task-code&_format=json",


    },
    'location': {
      'status': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/location-status&_format=json',
      'physicalType': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/location-physical-type&_format=json',
      'partOf': `${this.backendApiEndPoint}/Location?_format=json&_count=500`
    },
    'observation': {
      practitioner: "/encounter/encounter_participant.json",
      category: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/observation-category&_format=json",
      code: "/dummy.json",
      localCode: `${this.backendApiEndPoint}/CodeSystem?url=https://elikita-server.daalitech.com/CodeSystem/observation-loinc`,
      category2: "/observation/observation_category.json",
      category3: "/observation/observation_category.json",
      category4: "/observation/observation_category.json",

    },

    'allergy': {
      clinicalStatus: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergyintolerance-clinical&_format=json",
      verificationStatus: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergyintolerance-verification&_format=json",
      type: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergy-intolerance-type&_format=json",
      code: "dummy.json",
      category: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergy-intolerance-category&_format=json",
      criticality: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergy-intolerance-criticality&_format=json",
      manifestation: "dummy.json",
      exposureRoute: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/route-codes&_format=json",
      reactionSubstance: "dummy.json"
    },
    diagnosticReport: {
      'category': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/diagnostic-service-sections&_format=json",
      code: "dummy.json",
      performer: `${this.backendApiEndPoint}/Practitioner?_format=json&_count=1000`,
      resultsInterpreter: `${this.backendApiEndPoint}/Practitioner?_format=json&_count=1000`,
      specimen: `${this.backendApiEndPoint}/Specimen?_format=json`,
      observation: `${this.backendApiEndPoint}/Observation?_format=json`,
      rawObservation: `${this.backendApiEndPoint}/Observation?_format=json`,
      rawSpecimen: `${this.backendApiEndPoint}/Specimen?_format=json`,
    },
    medicine: {
      'code': "dummy.json",
      "doseForm": "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medication-form-codes&_format=json",
      "ingredientItem": "dummy.json",
      "ingredientStrength": "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medication-ingredientstrength&_format=json"
    },
    immunization: {
      statusReason: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-status-reason&_format=json",
      vaccineCode: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/vaccine-code&_format=json",
      informationSource: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-origin&_format=json",
      site: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-site&_format=json",
      route: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-route&_format=json",
      reason: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-reason&_format=json",
      targetDisease: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/immunization-target-disease&_format=json"
    },

    chargeItemDef: {
      currency: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/currencies&_format=json"
    }

  }

  baseFunctionToRetrieveValueset = (value: any) => {
    return this.valueSetToConcept(value).concept;
  }

  baseFunctionToRetrieveValueCodes = (value: any): string[] => {
    return this.valueSetToConcept(value).concept.map(c => c.code);
  }

  baseFunctionToRetrieveValueDelimited = (value: any): string[] => {
    return this.valueSetToConcept(value).concept.map(c => `${c.code ?? ''}$#$${c.display ?? ''}$#$${c.system ?? ''}`);
  }

  /**
   * Load observation codes from the backend CodeSystem URL and return
   * an array of { code, display, group? } sorted by display.
   * Handles both direct CodeSystem resource or a Bundle containing the CodeSystem.
   */
  getObservationCodeOptionsFromServer(): Promise<Array<{ code: string; display: string; group?: string }>> {
    const url = 'https://elikita-server.daalitech.com/CodeSystem/observation-loinc';
    return this.http.get<any>(url).toPromise().then((resp) => {
      if (!resp) return [];
      // If wrapped in a Bundle, find the CodeSystem resource
      let cs: any = null;
      if (resp.resourceType === 'Bundle' && Array.isArray(resp.entry)) {
        const found = resp.entry.find((e: any) => e?.resource?.resourceType === 'CodeSystem');
        cs = found?.resource ?? null;
      } else if (resp.resourceType === 'CodeSystem') {
        cs = resp;
      } else if (resp.entry && resp.entry[0] && resp.entry[0].resource && resp.entry[0].resource.resourceType === 'CodeSystem') {
        cs = resp.entry[0].resource;
      }

      const out: Array<{ code?: string; display?: string; group?: string }> = [];
      if (cs) {
        const concepts = cs.concept ?? cs.concept ?? cs.content ?? [];
        if (Array.isArray(concepts) && concepts.length) {
          for (const c of concepts) {
            out.push({ code: c.code ?? c.value ?? c.id ?? '', display: c.display ?? c.displayName ?? c.value ?? '' });
          }
        }
        // Some CodeSystems use 'concept' or 'concepts' or 'concept[].concept'
        if (!out.length && Array.isArray(cs.concept)) {
          for (const c of cs.concept) {
            out.push({ code: c.code ?? '', display: c.display ?? '' });
          }
        }
      }

      // Dedupe & sort
      const unique = new Map<string, { code: string; display: string; group?: string }>();
      for (const r of out) {
        const key = (r.code || r.display || '').toString().trim();
        if (!key) continue;
        if (!unique.has(key)) unique.set(key, { code: r.code || r.display || key, display: r.display || r.code || key, group: r.group });
      }
      return Array.from(unique.values()).sort((a, b) => (a.display || '').localeCompare(b.display || ''));
    }).catch(() => []);
  }

  /**
   * ValueSet/$expand can return a nested tree via `contains[].contains[]...`.
   * This flattens the tree into a simple Coding[] option list.
   */
  valueSetExpansionToFlatCodings(value: ValueSet | any): Coding[] {
    let topContains = value?.expansion?.contains;

    // If expansion is missing, try to collect from compose.include.concept
    if (!Array.isArray(topContains) || topContains.length === 0) {
      const composeItems: any[] = [];
      if (Array.isArray(value?.compose?.include)) {
        value.compose.include.forEach((inc: any) => {
          if (Array.isArray(inc.concept)) {
            inc.concept.forEach((c: any) => {
              composeItems.push({ ...c, system: inc.system || c.system });
            });
          }
        });
      }
      topContains = composeItems;
    }

    if (!Array.isArray(topContains) || topContains.length === 0) {
      if (Array.isArray(value?.concept)) {
        topContains = value.concept.map((c: any) => ({ ...c, system: value.url || '' }));
      } else if (value?.resourceType === 'Bundle' && value?.entry?.[0]?.resource?.concept) {
        const cs = value.entry[0].resource;
        topContains = cs.concept.map((c: any) => ({ ...c, system: cs.url || '' }));
      }
    }

    if (!Array.isArray(topContains) || topContains.length === 0) return [];

    const defaultSystem: string =
      topContains?.[0]?.system
      ?? value?.compose?.include?.[0]?.system
      ?? '';

    const flattened = this.flattenContainsTree(topContains, defaultSystem);

    // De-dupe by (system|code)
    const seen = new Map<string, Coding>();
    for (const coding of flattened) {
      const system = (coding.system || '').trim();
      const code = (coding.code || '').trim();
      if (!code) continue;
      const key = `${system}|${code}`;
      if (!seen.has(key)) {
        seen.set(key, { system, code, display: coding.display } as Coding);
      }
    }

    return Array.from(seen.values());
  }

  private flattenContainsTree(contains: any[], inheritedSystem: string): Coding[] {
    if (!Array.isArray(contains) || contains.length === 0) return [];

    const out: Coding[] = [];
    for (const node of contains) {
      if (!node) continue;
      const system = (node.system ?? inheritedSystem ?? '') as string;

      const code = node.code ?? '';
      const display = node.display ?? '';

      if (code) {
        out.push({ system, code, display } as Coding);
      }

      if (Array.isArray(node.contains) && node.contains.length) {
        out.push(...this.flattenContainsTree(node.contains, system));
      }
    }

    return out;
  }

  valueSetToConcept(value: ValueSet | any) {
    const concepts: { code: string; display: string; system: string }[] = [];

    const expansion = value?.expansion?.contains ?? [];
    expansion.forEach((item: any) => {
      if (item?.code) {
        concepts.push({
          code: item.code ?? '',
          display: item.display ?? '',
          system: item.system ?? ''
        });
      }
    });

    if (!concepts.length && Array.isArray(value?.compose?.include)) {
      value.compose.include.forEach((include: any) => {
        const includeConcepts = include?.concept ?? [];
        includeConcepts.forEach((concept: any) => {
          if (concept?.code) {
            concepts.push({
              code: concept.code ?? '',
              display: concept.display ?? '',
              system: include?.system ?? concept?.system ?? ''
            });
          }
        });
      });
    }

    const system = concepts.length ? concepts[0].system : (expansion?.[0]?.system ?? value?.compose?.include?.[0]?.system ?? '');

    return {
      system,
      concept: concepts
    };
  }

  practitionerBundleToReferenceData(value: Bundle<Practitioner> | null = null): ReferenceDataType[] {
    if (!value?.entry?.length) {
      return [];
    }

    return value.entry
      .map((entry: BundleEntry<Practitioner>) => {
        const resource = entry.resource;
        if (!resource) {
          return null;
        }

        const identifier = resource.identifier?.[0]?.value;
        const id = resource.id ?? identifier ?? '';
        if (!id) {
          return null;
        }

        const reference = `${resource.resourceType ?? 'Practitioner'}/${id}`;
        const primaryName = resource.name?.[0];
        const display = primaryName?.text
          ?? [primaryName?.given?.join(' '), primaryName?.family].filter(Boolean).join(' ').trim()
          ?? resource.id
          ?? 'Unnamed Practitioner';

        return {
          reference,
          display,
          // type: resource.resourceType ?? 'Practitioner'
        } as ReferenceDataType;
      })
      .filter((ref): ref is ReferenceDataType => !!ref?.reference);

  }

  practitionerBundleToDelimitedOptions(value: Bundle<Practitioner> | null = null): string[] {
    const references = this.practitionerBundleToReferenceData(value);
    return references
      .map(option => {
        const reference = option.reference ?? '';
        if (!reference) {
          return null;
        }
        const display = option.display ?? '';
        const type = option.type ?? 'Practitioner';
        return [reference, display, type].join('$#$');
      })
      .filter((entry): entry is string => !!entry);
  }

  patientBundleToReferenceData(value: Bundle<Patient> | null = null, allPatients: boolean = false): ReferenceDataType[] {
    if (!value?.entry?.length) return [];

    return value.entry
      .map((entry: BundleEntry<Patient>) => {
        const resource = entry.resource;
        if (!resource?.id) return null;

        const statusFlag = (resource as any)?.isActive ?? resource.active;
        const isActive =
          typeof statusFlag === 'string'
            ? statusFlag.toLowerCase() === 'active'
            : statusFlag === true;

        if (!isActive && !allPatients) return null;

        const name = resource.name?.[0];
        const display =
          name?.text
          || [name?.given?.join(' '), name?.family].filter(Boolean).join(' ').trim()
          || resource.id;

        return {
          reference: `${resource.resourceType ?? 'Patient'}/${resource.id}`,
          display,
          // type: resource.resourceType ?? 'Patient'
        } as Reference;
      })
      .filter((ref): ref is Reference => !!ref?.reference);
  }

  serviceRequestBundleToReferenceData(value: Bundle<ServiceRequest> | null = null): ReferenceDataType[] {
    if (!value?.entry?.length) return [];

    return value.entry
      .map((entry: BundleEntry<ServiceRequest>) => {
        const resource = entry.resource;
        if (!resource?.id) return null;

        const code = resource.code?.text
          || resource.code?.coding?.[0]?.display
          || resource.code?.coding?.[0]?.code
          || 'Unnamed Service Request';

        return {
          reference: `${resource.resourceType ?? 'ServiceRequest'}/${resource.id}`,
          display: code,
          type: resource.resourceType ?? 'ServiceRequest'
        } as ReferenceDataType;
      })
      .filter((ref): ref is ReferenceDataType => !!ref?.reference);
  }

  baseFunctionToTurnPractitionerIntoReference = (value: any = null) => {
    return this.practitionerBundleToDelimitedOptions(value);
  }

  //     } else if (value.hasOwnProperty('id')) {
  //       return `Practitioner/${value.id}`;
  //     }
  //     else {
  //       return "";
  //     }
  //   }
  // }

  retrieveCodeabeConcept(value: CodeableConcept | null = null) {
    if (value === null || value === undefined) {
      return "";
    } else {
      if (value.text) {
        return value.text;
      }
      if (value.coding?.[0]?.display) {
        return value.coding?.[0]?.display;
      }
      return value.coding?.[0]?.code ?? ''

    }
  }

  // statusReason, vaccineCode, informationSource, site, route, reason
  transformValues = {
    //   'location': {
    //   'status': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/location-status&_format=json',
    //   'physicalType': 'https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/location-physical-type&_format=json',
    //   'partOf': `${this.backendApiEndPoint}/Location?_format=json&_count=500`
    // },
    'location': {
      // 'status': this.baseFunctionToRetrieveValueset,
      'physicalType': this.baseFunctionToRetrieveValueset,
      'partOf': (value: Bundle<Location>) => {
        return value.entry?.map((e: BundleEntry<Location>) => {
          const display = e.resource?.name || 'Unnamed Location';
          const reference = "Location/" + e.resource?.id;
          return reference + "$#$" + display;
        })
      }


    },
    'healthcareService': {
      'practitioners': (value: Bundle<Practitioner>) => this.practitionerBundleToDelimitedOptions(value)
    },
    'referral': {
      'organization': (value: Bundle<HealthcareService>) => {
        return value.entry?.map((e: BundleEntry<HealthcareService>) => {
          const display = e.resource?.name || 'Unnamed Organization';
          const reference = "Organization/" + e.resource?.identifier?.[0]?.value;
          return reference + "$#$" + display;
        })
      }
    },
    'addClaim': {
      encounter: (value: Bundle<Encounter>) => {
        return value.entry?.map((e: BundleEntry<Encounter>) => {
          const display = "Encounter-" + (e.resource && e.resource.id ? e.resource.id : '') + (e.resource?.type?.[0]?.text ? " | " + e.resource?.type?.[0]?.text : '') + (e.resource?.subject?.display ? " | " + e.resource?.subject?.display : "");
          const reference = "Encounter/" + e.resource?.id;

          return reference + "$#$" + display
        })
      }
    },
    diagnosticReport: {
      category: this.baseFunctionToRetrieveValueset,
      observation: (val: any) => {
        return val;
      },
      specimen: (val: any) => {
        return val;
      },
      rawObservation: (val: any) => {
        return val;
      },
      rawSpecimen: (val: any) => {
        return val;
      },
      code: (val: any) => {
        return ["https://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/report-codes&_format=json"]
      },
      performer: this.baseFunctionToTurnPractitionerIntoReference,
      resultsInterpreter: this.baseFunctionToTurnPractitionerIntoReference
    },

    "chargeItemDef": {
      currency: this.baseFunctionToRetrieveValueset
    },
    "immunization": {
      statusReason: this.baseFunctionToRetrieveValueset,
      vaccineCode: this.baseFunctionToRetrieveValueset,
      informationSource: this.baseFunctionToRetrieveValueset,
      site: this.baseFunctionToRetrieveValueset,
      route: this.baseFunctionToRetrieveValueset,
      reason: this.baseFunctionToRetrieveValueset,
      targetDisease: this.baseFunctionToRetrieveValueset
    },
    'condition': {
      code: (val: any) => {
        return [this.patientBackendEndPoint.replace("api/fhir", "") + "api/icd11/codings"]
      },
      severity: (value: ValueSet | any) => this.valueSetExpansionToFlatCodings(value),
      clinicalStatus: (value: ValueSet | any) => this.valueSetExpansionToFlatCodings(value),
      verificationStatus: (value: ValueSet | any) => this.valueSetExpansionToFlatCodings(value)

    },
    "medicine": {
      doseForm: this.baseFunctionToRetrieveValueset,
      ingredientStrength: this.baseFunctionToRetrieveValueset,
      ingredientItem: (val: any) => {
        return ["https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD&purpose=INGREDIENT"]


      },
      "code": (val: any) => {
        return ["https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD"]
      }
    },
    'allergy': {
      clinicalStatus: this.baseFunctionToRetrieveValueset,
      verificationStatus: this.baseFunctionToRetrieveValueset,
      type: this.baseFunctionToRetrieveValueset,
      category: this.baseFunctionToRetrieveValueset,
      exposureRoute: this.baseFunctionToRetrieveValueset,
      patient: (val: Bundle<Patient>) => {
        return val.entry?.map((e: BundleEntry<Patient>) => {
          const display = e.resource?.name?.[0].family || "unknown";
          const reference = "Patient/" + e.resource?.identifier?.[0].value;
          return reference + "$#$" + display
        })
      },
      code: (value: any) => {
        return ["https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/allergyintolerance-code&_format=json"];
      },
      manifestation: (val: any) => {
        return ["https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/clinical-findings&_format=json"];
      },
      reactionSubstance: (val: any) => {
        return "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/substance-code&_format=json"
      }

    },
    'observation': {
      practitioner:

        (all: any) => {
          if (all.hasOwnProperty('system') && all.hasOwnProperty('property')) {
            all = {
              ...all, concept: all.concept.map((e: any) => {

                const system = all.system;
                return { ...e, system }


              })
            }
          } else {
            all = all
          }

          return all;
        },
      category: this.baseFunctionToRetrieveValueset,
      // (all: any) => {

      //   if (all.hasOwnProperty('system') && all.hasOwnProperty('property')) {
      //     all = {
      //       ...all, concept: all.concept.map((e: any) => {

      //         const system = all.system;
      //         return { ...e, system }


      //       })
      //     }
      //   } else {
      //     all = this.baseFunctionToRetrieveValueset;
      //   }
// $#$      //   // /* alert(JSON.stringify(all) */);
      //   return all;
      // },
      code:
        (all: any) => {
          // if (all.hasOwnProperty('system') && all.hasOwnProperty('property')) {
          //   all = {
          //     ...all, concept: all.concept.map((e: any) => {

          //       const system = all.system;
          //       return { ...e, system }


          //     })
          //   }
          // } else {
          //   all = all
          // }

          // return all;
          return ["https://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/observation-codes&_format=json"]
        },
      localCode: (all: any) => {
        if (all.codings) return all.codings;
        if (all.resourceType === 'Bundle' && all.entry?.[0]?.resource?.concept) {
          const cs = all.entry[0].resource;
          // Force standard loinc system instead of using the local cs.url
          return cs.concept.map((c: any) => ({ ...c, system: 'http://loinc.org' }));
        }
        return [];
      },

      category2:
        (all: any) => {

          all.concept = all.concept.filter((e: any) => {

            return e.display.toLowerCase().includes('vital')
              || e.display.toLowerCase().includes('exam')


          });

          return all;
        },
      category3:
        (all: any) => {

          all.concept = all.concept.filter((e: any) => {

            return e.display.toLowerCase().includes('exam')


          });

          return all;
        },

      category4:
        (all: any) => {

          all.concept = all.concept.filter((e: any) => {

            return e.display.toLowerCase().includes('laboratory')


          });

          return all;
        }



      ,

    },
    activityDefinition: {
      itemsRequired: (e: Bundle<SupplyDelivery>) => {
        return e.entry?.filter(e => {
          return e.resource?.suppliedItem?.itemCodeableConcept || (e.resource?.suppliedItem?.itemReference && e.resource?.suppliedItem?.itemReference?.display)
        }).map(e => {
          if (e.resource?.suppliedItem?.itemCodeableConcept) {
            if (e.resource?.suppliedItem?.itemCodeableConcept?.coding) {
              return {
                code: e.resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.code,
                display: (e.resource?.suppliedItem?.itemCodeableConcept?.text || e.resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.display) + `${e.resource?.suppliedItem?.quantity?.unit ? ' ( in ' + e.resource?.suppliedItem.quantity?.unit + ' )' : ""}`,
                system: `${this.backendApiEndPoint}`
              }
            } else {
              return {
                code: e.resource?.suppliedItem?.itemCodeableConcept?.text,
                display: e.resource?.suppliedItem?.itemCodeableConcept?.text + `${e.resource?.suppliedItem?.quantity?.unit ? ' ( in ' + e.resource?.suppliedItem.quantity?.unit + ' )' : ""}`,
                system: `${this.backendApiEndPoint}`
              }

            }
          } else {

            return {
              code: e.resource?.suppliedItem?.itemReference?.display,
              display: e.resource?.suppliedItem?.itemReference?.display + `${e.resource?.suppliedItem?.quantity?.unit ? ' ( in ' + e.resource?.suppliedItem.quantity?.unit + ' )' : ""}`,
              system: `${this.backendApiEndPoint}`
            }

          }
        })
      }

    },

    supplyDelivery: {
      substanceCode: (value: Bundle<ValueSet>) => {
        console.log("filtering substance codes from value set bundle", value);
        return (value?.entry?.[0]?.resource?.compose?.include?.[0]?.concept || []).map((item: any) => {
          console.log(item);
          return `${item.code ?? ''}$#$${item.display ?? ''}$#$${item.system ?? `${this.terminologyServer}`}`;
        }) || [];
      },
      // substanceCodeAll: (value: any) => {
      //   return (value?.expansion?.contains || []).map((item: any) => {
      //     return `${item.code ?? ''}$#$${item.display ?? ''}$#$${item.system ?? ''}`;
      //   }) || [];
      // }
    },
    admission: {
      // ward: (value: Bundle<Location>) => {
      //   return value.entry?.filter((entry: BundleEntry<Location>) => {
// $#$      //     // /* alert(this.retrieveCodeabeConcept(entry.resource?.form) */.toLowerCase());
      //     return this.retrieveCodeabeConcept((entry.resource as any)?.form).toLowerCase().includes('ward');
      //   }).map((entry: BundleEntry<Location>) => {
// $#$      //     // /* alert(entry.resource?.name || 'Unnamed Location'); */
      //     const rd = {
      //       reference: `Location/${entry.resource?.identifier?.[0]?.value}`,
      //       display: entry.resource?.name || 'Unnamed Location'
      //     };
      //     return `${rd.reference}$#$${rd.display}$#$${entry.resource?.partOf?.display || ''}`;

      //   }) || []
      // },

      // room: (value: Bundle<Location>) => {
      //   return value.entry?.filter((entry: BundleEntry<Location>) => {
      //     return this.retrieveCodeabeConcept((entry.resource as any)?.form).toLowerCase().includes('room');
      //   }).map((entry: BundleEntry<Location>) => {
      //     const rd = {
      //       reference: `Location/${entry.resource?.identifier?.[0]?.value}`,
      //       display: entry.resource?.name || 'Unnamed Location',
      //       partOf: entry.resource?.partOf?.display || ''
      //     };
      //     return `${rd.reference}$#$${rd.display}$#$${rd.partOf}`;

      //   }) || []
      // },

      // bed: (value: Bundle<Location>) => {
      //   return value.entry?.filter((entry: BundleEntry<Location>) => {
      //     return this.retrieveCodeabeConcept((entry.resource as any)?.form).toLowerCase().includes('bed');
      //   }).map((entry: BundleEntry<Location>) => {
      //     const rd = {
      //       reference: `Location/${entry.resource?.identifier?.[0]?.value}`,
      //       display: entry.resource?.name || 'Unnamed Location',
      //       partOf: entry.resource?.partOf?.display || ''
      //     };
      //     return `${rd.reference}$#$${rd.display}$#$${rd.partOf}`;
      //   }) || []
      // },


      location: (value: Bundle<Location>) => {
        return value.entry?.map((entry: BundleEntry<Location>) => {
          // return {
          //   reference: `Location/${entry.resource?.identifier?.[0]?.value}`,
          //   display: entry.resource?.name || 'Unnamed Location'
          // }
          return entry.resource
        }) || []
      },
      admitSource: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      carePlanStatus: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      carePlanIntent: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      taskCode: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      taskIntent: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      taskPriority: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      taskStatus: (value: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
    },
    encounter: {
      class: (value: ValueSet) => {
        return this.valueSetToConcept(value);
      },
      priority: (value: ValueSet) => {
        return this.valueSetToConcept(value);
      },
      participant: (value: Bundle<Practitioner>) => {
        return this.practitionerBundleToReferenceData(value);
      },
      reason: (_value: any) => {
// $#$        // /* alert('encounter reason called'); */
        return ['https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/encounter-reason&_format=json'];
      },
      reason_use: (_value: any) => {
        return [
          "CC$#$Chief Complaint$#$http://hl7.org/fhir/encounter-reason-use",
          "HC$#$Health Concern$#$http://hl7.org/fhir/encounter-reason-use",
          "AD$#$Admitting Diagnosis$#$http://hl7.org/fhir/encounter-reason-use",
          "RV$#$Reason for Visit$#$http://hl7.org/fhir/encounter-reason-use",
          "HM$#$Health Maintenance$#$http://hl7.org/fhir/encounter-reason-use"
        ];

      }

    },
    appointment: {
      'status': (val: ValueSet) => {
        return this.baseFunctionToRetrieveValueCodes(val);
      },
      appointmentType: (val: Bundle<CodeSystem>) => {
        ////console.log(val.entry, 'Slot Appointmenttype')

        const originalConceptArray = val.entry?.[0]?.resource?.concept?.map((cc => {
          //console.log(cc);
          return `${cc.display}$#$${cc.code}`
        })) || []
        originalConceptArray?.push('Others');
        return originalConceptArray;

      },
      'participantActor': (val: Bundle<Practitioner>) => {
        const allRef = val.entry?.map((serviceType: BundleEntry<Practitioner>) => {
          const ref = `Practitioner/${serviceType.resource?.id}`
// $#$          // /* alert(ref); */
          const display = `${serviceType.resource?.name?.[0]?.given?.join(" ")} ${serviceType.resource?.name?.[0]?.family} `
// $#$          // /* alert(display); */
          const Referenced: Reference = {
            reference: ref,
            display
          }
          return `${Referenced.reference}$#$${Referenced.display}`
        }) || []
// $#$        //    /* alert(allRef.join(" ") */)
        return [...allRef, 'Others']



      },
      serviceType: (val: Bundle<HealthcareService>) => {
        const allRef = val.entry?.map((serviceType: BundleEntry<HealthcareService>) => {
          const ref = `HealthcareService/${serviceType.resource?.id}`
          const display = serviceType.resource?.name ?? 'UnLabeled Service'
          const Referenced: Reference = {
            reference: ref,
            display
          }
          return `${Referenced.reference}$#$${Referenced.display}`
        }) || []
        return [...allRef, 'Others']


      },

      'reason': (value: any) => {
        return [this.patientBackendEndPoint.replace("api/fhir", "") + "api/icd11/codings"]
        // return value.expansion.contains.map((item: any) => {
        //   // return {
        //   //   code: item.code,
        //   //   display: item.display,
        //   //   system: item.system
        //   // };
// $#$        //   ////* alert(`${item.code}$#$${item.display}$#$${item.system}`); */
        //   return `${item.code}$#$${item.display}$#$${item.system}`;
        //}
        // );

      }


    },

    slot: {
      serviceType: (val: Bundle<HealthcareService>) => {
        const allRef = val.entry?.map((serviceType: BundleEntry<HealthcareService>) => {
          const ref = `HealthcareService/${serviceType.resource?.id}`
          const display = serviceType.resource?.name ?? 'UnLabeled Service'
          const Referenced: Reference = {
            reference: ref,
            display
          }
          return `${Referenced.reference}$#$${Referenced.display}`
        }) || []
        return [...allRef, 'Others']


      },

      appointmentType: (val: Bundle<CodeSystem>) => {
        //console.log(val.entry, 'Slot Appointmenttype')

        const originalConceptArray = val.entry?.[0]?.resource?.concept?.map((cc => {
          //console.log(cc);
          return `${cc.display}$#$${cc.code}`
        })) || []
        originalConceptArray?.push('Others');
        return originalConceptArray;

      }

    },
    patient: {
      //  'patient': {
      //       'gender': "https://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/administrative-gender&_format=json",
      //       'maritalStatus': "https://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/marital-status&_format=json",
      //       'relationship': "https://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/patient-contactrelationship&_format=json",
      //       'general_practitioner': `${this.backendApiEndPoint}/Practitioner?_format=json`,

      gender: (value: any) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      'maritalStatus': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'relationship': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'general_practitioner': (value: any) => {
        //console.log(value);
        return value.entry?.filter((e: any) => {
          if (e.resource.name && e.resource.name.length && e.resource.name[0].given && e.resource.name[0].given.length
            && e.resource.name[0].family
          ) {
            return true;
          } else {
            return false;
          }
        }).map((item: any) => {
          //console.log(item, "in subject");
          return `${item.fullUrl}$#$${item.resource.name[0].given[0]} ${item.resource.name[0].family}`;
        });
      },
      'organization': (value: Bundle<HealthcareService>) => {
        return value.entry?.map((e: BundleEntry<HealthcareService>) => {
          const display = e.resource?.name || 'Unnamed Organization';
          const reference = "Organization/" + e.resource?.id;
          return reference + "$#$" + display;
        })
      },



      'contactPointUse': (value: any) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },
      'contactPointSystem': (value: any) => {
        return this.baseFunctionToRetrieveValueCodes(value);
      },

    },
    specimen: {
      'status': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'type': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'request': (value: any) => {
        return this.serviceRequestBundleToReferenceData(value);
      },
      'subject': (value: any) => {
        // //console.log(value);
        return this.practitionerBundleToReferenceData(value);
      },

      'collector': (value: any) => {
        //console.log(value);
        return value;
      },
      'bodySite': (value: any) => {
        return ['http://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/body-site&_format=json']

      },

      'condition': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },


    },
    medication_administration: {
      // 'status': "http://hapi.fhir.org/baseR4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medication-admin-status&_format=json",
      // 'medication': "/dummy.json",
      // 'subject': `${this.backendApiEndPoint}/Patient?_format=json`,
      // "performer": `${this.backendApiEndPoint}/Practitioner?_format=json`,
      // "request": `${this.backendApiEndPoint}/MedicationRequest?_format=json`

      'status': this.baseFunctionToRetrieveValueDelimited,
      'medication': (value: any) => {
        return ['http://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medication-codes&_format=json']
      },


      'subject': (value: any) => {
        return value.entry.filter((e: any) => {
          if (e.resource.name && e.resource.name.length && e.resource.name[0].given && e.resource.name[0].given.length
            && e.resource.name[0].family
          ) {
            return true;
          } else {
            return false;
          }
        }).map((item: any) => {
          //console.log(item, "in subject");
          return `${item.fullUrl}$#$${item.resource.name[0].given[0]} ${item.resource.name[0].family}`;
        });
      },

      'performer': (value: any) => {
        return value.entry.filter((e: any) => {
          if (e.resource.name && e.resource.name.length && e.resource.name[0].given && e.resource.name[0].given.length
            && e.resource.name[0].family
          ) {
            return true;
          } else {
            return false;
          }
        }).map((item: any) => {
          //console.log(item, "in subject");
          return `${item.fullUrl}$#$${item.resource.name[0].given[0]} ${item.resource.name[0].family}`;
        });
      },
      'request': (value: any) => {
        return value.entry.map((item: any) => {

          return `${item.fullUrl}`;
        });
      },

    },
    medication: {
      // 'medication': (value: any) => {
      //   ////console.log(value);
      //   return value.expansion.contains.map((item: any) => {
      //     // return {
      //     //   code: item.code,
      //     //   display: item.display,
      //     //   system: item.system
      //     // };
// $#$      //     ////* alert(`${item.code}$#$${item.display}$#$${item.system}`); */
      //     return `${item.code}$#$${item.display}$#$${item.system}`;
      //   });
      // },
      'medication': (value: any) => {
        return ['https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD']
      },
      'performerType': (value: any) => {
        return value.concept.map((item: any) => {

          return {
            code: item.code,
            display: item.display,
            system: value.url
          };
        });
      },

      'reason': (value: any) => {
        // return ['https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/condition-code&_format=json']
        return [this.patientBackendEndPoint.replace("api/fhir", "") + "api/icd11/codings"]
        // return value.expansion.contains.map((item: any) => {
        //   // return {
        //   //   code: item.code,
        //   //   display: item.display,
        //   //   system: item.system
        //   // };
// $#$        //   ////* alert(`${item.code}$#$${item.display}$#$${item.system}`); */
        //   return `${item.code}$#$${item.display}$#$${item.system}`;
        //}
        // );

      }





    },
    //  'status': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medicationdispense-status&_format=json",
    //   'subject': this.backendApiEndPoint + "/Patient?_format=json",
    //   'category': "https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/medicationdispense-category&_format=json",
    //   // 'receiver': `${this.backendApiEndPoint}/Patient?_format=json`,
    //   // 'medication': "https://snowstorm.ihtsdotools.org/fhir/ValueSet/$expand?url=http://snomed.info/sct?fhir_vs=isa/763158003&_format=json",
    //   'medication': "dummy.json",
    //   medicationReference: this.backendApiEndPoint + "/Medication?_format=json",
    //   performer: this.backendApiEndPoint + "/Practitioner?_format=json",
    //   authorizingPrescription: this.backendApiEndPoint + "/MedicationRequest?_format=json",
    //   substitutionReason: "https://tx.fhir.org/r4/ValueSet/$expand?url=http://terminology.hl7.org/ValueSet/v3-SubstanceAdminSubstitutionReason&_format=json",
    //   substitutionType:
    'medication_dispense': {
      'medicationReference': (value: any) => {
        // return value?.entry?.filter((item: BundleEntry<Medication>) => {
        //   //out of stock filtered out
        //   return (item?.resource?.extension?.find((ext: any) => ext.url?.endsWith('totalRemaining'))?.valueQuantity?.value || 1) > 0;
        // })
        return (this.utilityService.filterAvailableMedications(value?.entry) as BundleEntry<Medication>[]).map((item: BundleEntry<Medication>) => {
          return `Medication/${item?.resource?.id || item.resource?.identifier?.[0]?.value}$#$${item?.resource?.code?.text || item?.resource?.code?.coding?.[0]?.display || 'Unnamed Medication'}`;
        }) || [];

      },
      performer: (value: any) => {
        return this.practitionerBundleToReferenceData(value);
      },
      authorizingPrescription: (value: any) => {
        return value.entry?.map((item: BundleEntry<MedicationRequest>) => {
          return `MedicationRequest/${item?.resource?.id || item.resource?.identifier?.[0]?.value}}$#$${item?.resource?.medicationCodeableConcept?.text || item?.resource?.medicationCodeableConcept?.coding?.[0]?.display || 'Unnamed MedicationRequest'}`;
        }) || [];
      },
      'substitutionReason': (value: any) => {
        return this.baseFunctionToRetrieveValueset(value);
      },
      'substitutionType': (value: any) => {
        return this.baseFunctionToRetrieveValueset(value);
      },
      'status': this.baseFunctionToRetrieveValueDelimited,
      'category': (value: any) => {
        return this.baseFunctionToRetrieveValueset(value);
      },

      'subject': (value: any) => {
        return this.patientBundleToReferenceData(value, true);
        return value.entry.map((item: any) => {

          //console.log(item, "in subject");
          return `${item.fullUrl}$#$${item.resource.name[0].given[0]} ${item.resource.name[0].family}`;
        });
      },
      'receiver': (value: any) => {
        return this.patientBundleToReferenceData(value);
        return value.entry.map((item: any) => {

          return `${item.fullUrl}$#$${item.resource.name[0].given[0]} ${item.resource.name[0].family}`;
        });
      },
      'medication': (value: any) => {
        return ["https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD&purpose=INGREDIENT"]


        ////console.log(value);
        // return value.expansion.contains.map((item: any) => {
        //   // return {
        //   //   code: item.code,
        //   //   display: item.display,
        //   //   system: item.system
        //   // };
// $#$        //   ////* alert(`${item.code}$#$${item.display}$#$${item.system}`); */
        //   return `${item.code}$#$${item.display}$#$${item.system}`;
        // });
      },
    },
    serviceRequest: {
      'status': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'intent': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'priority': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'performerType': (value: any) => {
        return this.baseFunctionToRetrieveValueDelimited(value);
      },
      'code': (value: any) => {
        return ['https://tx.fhir.org/r4/ValueSet/$expand?url=http://hl7.org/fhir/ValueSet/procedure-code&_FORMAT=JSON']
      },
      'reason': (_value: any) => {
        return [
          this.patientBackendEndPoint.replace("api/fhir", "") + "api/icd11/codings"
        ];
      },

    }
  }


  // single HttpClient injection is declared at the top as `private http`

  constructor() {

  }

  getFormFieldSelectData(...fieldPaths: string[]): Observable<any> {
    if (fieldPaths.length) {
      if (this.checkIfPropertiesExist(this.allUrls, ...fieldPaths)) {
        const url = this.getDeepValues(this.allUrls, ...fieldPaths);
        //console.log('URL:', url);
        return this.http.get(url, {
          context: new HttpContext().set(LoadingUIEnabled, true).set(DisableCountParam, true)

        }).pipe(map((data) => {
          if (this.checkIfPropertiesExist(this.transformValues, ...fieldPaths)) {
            const transformedValue = this.getDeepValues(this.transformValues, ...fieldPaths)(data);
            //console.log(transformedValue);
            return transformedValue;


          } else {
            return data;
            //console.log(data);
          }
        }));
      }
      else {
        //console.error('No url exists for the given field paths', fieldPaths);
        return throwError(() => new Error('No url exists for the given field paths: ' + JSON.stringify(fieldPaths)));
      }

    } else {
      return throwError(() => new Error('No field paths provided'));
    }

  }


  getDeepValues(object: { [key: string]: any }, ...deepeningProperties: any[]) {
    if (this.checkIfPropertiesExist(object, ...deepeningProperties)) {
      let highLevelObject = object[deepeningProperties[0]]
      let highLevelPropertyIndex = 1;
      while (highLevelPropertyIndex < deepeningProperties.length) {
        highLevelObject = highLevelObject[deepeningProperties[highLevelPropertyIndex]]
        highLevelPropertyIndex++;


      }
      return highLevelObject;

    } else {
      return null;
    }
  }

  utilityService = inject(UtilityService);
  checkIfPropertiesExist(object: { [key: string]: any }, ...deepeningProperties: any[]) {
    //console.log('Checking if properties exist:', deepeningProperties);
    if (deepeningProperties.length) {
      let highLevelProperty = 0;
      let allPropertiesExists = false;
      while (highLevelProperty < deepeningProperties.length) {
        //console.log('Checking property:', deepeningProperties[highLevelProperty],);
        if (highLevelProperty === 0) {
          object = object[deepeningProperties[highLevelProperty]];
          //console.log('Current object:', object);
          highLevelProperty++;
          allPropertiesExists = true;
        } else {


          if (object.hasOwnProperty(deepeningProperties[highLevelProperty])) {
            // //console.log('past object:', object);
            // //console.log('Property exists:', deepeningProperties[highLevelProperty]);
            allPropertiesExists = true;

            object = object[deepeningProperties[highLevelProperty]];
            // //console.log('Current object:', object);
            highLevelProperty++;
          } else {
            allPropertiesExists = false;
            break;
            //console.error('Property does not exist:', deepeningProperties[highLevelProperty]);
          }


          // return object[deepeningProperties[highLevelProperty - 1]].hasOwnProperty(deepeningProperties[highLevelProperty])
        }
      }



      return allPropertiesExists;
    } else {
      return false;
    }


  }
}
